import type { CanalePanoramica, KpiCanale, PuntoAndamento } from "@/lib/api";
import type { FiltroProdotti } from "@/lib/filtriOfferta";

export type Paese = "tutti" | "IT" | "ES" | "FR";
export type Vista = "stato" | "dati" | "automazione";
export type Metrica =
  | "sotto_margine"
  | "recuperabili"
  | "al_limite"
  | "attaccate_al_floor"
  | "vinte_pct"
  | "spente";
export type StatoCanale = "in_calcolo" | "errore" | "vecchio" | "ok";
export type ChiaveDelta = "sotto_margine" | "recuperabili" | "al_limite" | "da_correggere";

const MENO = "−";
const GIORNO_MS = 86_400_000;
// Numeri piu' vecchi di due giri orari: il ricalcolo si e' fermato o salta i turni.
export const NUMERI_VECCHI_MS = 2 * 60 * 60 * 1000;

export function nf(n: number, decimali = 0): string {
  return new Intl.NumberFormat("it-IT", {
    minimumFractionDigits: decimali,
    maximumFractionDigits: decimali,
    useGrouping: true,
  })
    .format(n)
    .replace(/-/g, MENO);
}

export function pf(v: number | string): string {
  return `${nf(Number(v), 1)}%`;
}

export function ef(v: number | string): string {
  return `${nf(Math.round(Number(v)))} €`;
}

export function df(v: number | null): string | null {
  if (v === null) return null;
  if (v === 0) return "0";
  return `${v > 0 ? "+" : MENO}${nf(Math.abs(v))}`;
}

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

export function giornoBreve(iso: string): string {
  const [, mese, giorno] = iso.slice(0, 10).split("-").map(Number);
  return `${giorno} ${MESI[mese - 1]}`;
}

export function oraBreve(iso: string): string {
  return new Date(iso).toLocaleTimeString("it-IT", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Rome",
  });
}

export function giorniDa(iso: string, adesso: Date): number {
  return Math.max(0, Math.floor((adesso.getTime() - new Date(iso).getTime()) / GIORNO_MS));
}

export function statoCanale(c: CanalePanoramica, adesso: Date): StatoCanale {
  const calcolato = c.calcolato_at ? new Date(c.calcolato_at).getTime() : 0;
  if (c.ultimo_errore_at && new Date(c.ultimo_errore_at).getTime() > calcolato) return "errore";
  if (c.kpi === null) return "in_calcolo";
  if (adesso.getTime() - calcolato > NUMERI_VECCHI_MS) return "vecchio";
  return "ok";
}

export function freschezzaPct(k: Pick<KpiCanale, "offerte" | "controllate_24h">): number {
  return k.offerte > 0 ? Math.round((k.controllate_24h / k.offerte) * 100) : 100;
}

export function perPaese(canali: CanalePanoramica[], paese: Paese): CanalePanoramica[] {
  return paese === "tutti" ? canali : canali.filter((c) => c.country === paese);
}

export function delta(c: CanalePanoramica, chiave: ChiaveDelta): number | null {
  return c.kpi && c.ieri ? c.kpi[chiave] - c.ieri[chiave] : null;
}

export function deltaTotale(canali: CanalePanoramica[], chiave: ChiaveDelta): number | null {
  const valori = canali.map((c) => delta(c, chiave)).filter((v): v is number => v !== null);
  return valori.length ? valori.reduce((a, b) => a + b, 0) : null;
}

// I numeri di una riga di tabella, uguali per un canale e per il totale. Le
// percentuali e gli euro "peggiori" nel totale sono il caso peggiore fra i canali:
// le mediane non si sommano.
export interface NumeriRiga {
  offerte: number;
  spente: number | null;
  riaprono_sotto_margine: number;
  vinte: number;
  vinte_pct: number | null;
  attaccate_al_floor: number;
  recuperabili: number;
  recuperabili_auto: number;
  motivi_blocco: Record<string, number>;
  al_limite: number;
  al_limite_pct: number | null;
  perse_altro: number;
  senza_stato: number;
  a_margine: number;
  da_correggere: number;
  senza_floor: number;
  sotto_margine: number;
  sotto_margine_pct: number | null;
  sotto_margine_eur: number | null;
  opportunita: number;
  senza_costo: number;
  senza_spedizione: number;
  prezzi_non_misurati: number;
  solo_listino: number;
  dati_mancanti: number;
  controllate_24h: number;
  scritti_auto: number;
  scritti_manuali: number;
  fallite: number;
  in_attesa: number;
  in_attesa_dal: string | null;
  delta_sotto_margine: number | null;
  delta_recuperabili: number | null;
  delta_al_limite: number | null;
  delta_da_correggere: number | null;
}

function percentualeVinte(vinte: number, offerte: number, spente: number | null): number | null {
  const acquistabili = offerte - (spente ?? 0);
  return acquistabili > 0 ? (vinte / acquistabili) * 100 : null;
}

function numero(v: string | null): number | null {
  return v === null ? null : Number(v);
}

export function numeriCanale(c: CanalePanoramica): NumeriRiga | null {
  const k = c.kpi;
  if (k === null) return null;
  return {
    offerte: k.offerte,
    spente: k.spente,
    riaprono_sotto_margine: k.riaprono_sotto_margine,
    vinte: k.vinte,
    vinte_pct: percentualeVinte(k.vinte, k.offerte, k.spente),
    attaccate_al_floor: k.attaccate_al_floor,
    recuperabili: k.recuperabili,
    recuperabili_auto: k.recuperabili_auto,
    motivi_blocco: k.motivi_blocco,
    al_limite: k.al_limite,
    al_limite_pct: numero(k.al_limite_mediana_pct),
    perse_altro: k.perse_altro,
    senza_stato: k.senza_stato,
    a_margine: k.a_margine,
    da_correggere: k.da_correggere,
    senza_floor: k.senza_floor,
    sotto_margine: k.sotto_margine,
    sotto_margine_pct: numero(k.sotto_margine_mediana_pct),
    sotto_margine_eur: numero(k.sotto_margine_peggiore_eur),
    opportunita: k.opportunita,
    senza_costo: k.senza_costo,
    senza_spedizione: k.senza_spedizione,
    prezzi_non_misurati: k.prezzi_non_misurati,
    solo_listino: k.solo_listino,
    dati_mancanti: k.dati_mancanti,
    controllate_24h: k.controllate_24h,
    scritti_auto: k.scritti_auto,
    scritti_manuali: k.scritti_manuali,
    fallite: k.fallite,
    in_attesa: k.in_attesa,
    in_attesa_dal: k.in_attesa_dal,
    delta_sotto_margine: delta(c, "sotto_margine"),
    delta_recuperabili: delta(c, "recuperabili"),
    delta_al_limite: delta(c, "al_limite"),
    delta_da_correggere: delta(c, "da_correggere"),
  };
}

export function numeriTotale(canali: CanalePanoramica[]): NumeriRiga {
  const righe = canali.map(numeriCanale).filter((n): n is NumeriRiga => n !== null);
  const somma = (campo: (n: NumeriRiga) => number) => righe.reduce((a, n) => a + campo(n), 0);
  const peggiore = (campo: (n: NumeriRiga) => number | null) => {
    const valori = righe.map(campo).filter((v): v is number => v !== null);
    return valori.length ? Math.min(...valori) : null;
  };
  const spenteNote = righe.filter((n) => n.spente !== null);
  const spente = spenteNote.length ? spenteNote.reduce((a, n) => a + (n.spente ?? 0), 0) : null;
  const motivi: Record<string, number> = {};
  for (const n of righe) {
    for (const [motivo, quanti] of Object.entries(n.motivi_blocco)) {
      motivi[motivo] = (motivi[motivo] ?? 0) + quanti;
    }
  }
  const attese = righe
    .map((n) => n.in_attesa_dal)
    .filter((d): d is string => d !== null)
    .sort();
  const offerte = somma((n) => n.offerte);
  const vinte = somma((n) => n.vinte);
  return {
    offerte,
    spente,
    riaprono_sotto_margine: somma((n) => n.riaprono_sotto_margine),
    vinte,
    vinte_pct: percentualeVinte(vinte, offerte, spente),
    attaccate_al_floor: somma((n) => n.attaccate_al_floor),
    recuperabili: somma((n) => n.recuperabili),
    recuperabili_auto: somma((n) => n.recuperabili_auto),
    motivi_blocco: motivi,
    al_limite: somma((n) => n.al_limite),
    al_limite_pct: peggiore((n) => n.al_limite_pct),
    perse_altro: somma((n) => n.perse_altro),
    senza_stato: somma((n) => n.senza_stato),
    a_margine: somma((n) => n.a_margine),
    da_correggere: somma((n) => n.da_correggere),
    senza_floor: somma((n) => n.senza_floor),
    sotto_margine: somma((n) => n.sotto_margine),
    sotto_margine_pct: peggiore((n) => n.sotto_margine_pct),
    sotto_margine_eur: peggiore((n) => n.sotto_margine_eur),
    opportunita: somma((n) => n.opportunita),
    senza_costo: somma((n) => n.senza_costo),
    senza_spedizione: somma((n) => n.senza_spedizione),
    prezzi_non_misurati: somma((n) => n.prezzi_non_misurati),
    solo_listino: somma((n) => n.solo_listino),
    dati_mancanti: somma((n) => n.dati_mancanti),
    controllate_24h: somma((n) => n.controllate_24h),
    scritti_auto: somma((n) => n.scritti_auto),
    scritti_manuali: somma((n) => n.scritti_manuali),
    fallite: somma((n) => n.fallite),
    in_attesa: somma((n) => n.in_attesa),
    in_attesa_dal: attese[0] ?? null,
    delta_sotto_margine: deltaTotale(canali, "sotto_margine"),
    delta_recuperabili: deltaTotale(canali, "recuperabili"),
    delta_al_limite: deltaTotale(canali, "al_limite"),
    delta_da_correggere: deltaTotale(canali, "da_correggere"),
  };
}

export function hrefProdotti(canale: string, filtro?: FiltroProdotti): string {
  const query = new URLSearchParams({ canale });
  if (filtro) query.set("filtro", filtro);
  return `/prodotti?${query.toString()}`;
}

export function hrefStorico(canale: string, tipo: "scritti" | "fallite"): string {
  return `/history?${new URLSearchParams({ canale, tipo }).toString()}`;
}

export function hrefDaApprovare(canale: string): string {
  return `/approvals?${new URLSearchParams({ canale }).toString()}`;
}

export const METRICHE: ReadonlyArray<{ id: Metrica; label: string; soloBuybox: boolean }> = [
  { id: "sotto_margine", label: "Sotto margine", soloBuybox: false },
  { id: "recuperabili", label: "Recuperabili", soloBuybox: true },
  { id: "al_limite", label: "Al limite", soloBuybox: true },
  { id: "attaccate_al_floor", label: "Attaccate al floor", soloBuybox: true },
  { id: "vinte_pct", label: "% vinte", soloBuybox: true },
  { id: "spente", label: "Spente", soloBuybox: true },
];

export interface Serie {
  giorni: string[];
  totale: Array<number | null>;
  canale: Array<number | null> | null;
}

function valoreDelGiorno(punti: PuntoAndamento[], metrica: Metrica): number | null {
  if (punti.length === 0) return null;
  if (metrica === "vinte_pct") {
    const offerte = punti.reduce((a, p) => a + p.offerte, 0);
    const spente = punti.reduce((a, p) => a + (p.spente ?? 0), 0);
    return percentualeVinte(punti.reduce((a, p) => a + p.vinte, 0), offerte, spente);
  }
  if (metrica === "spente") {
    const note = punti.filter((p) => p.spente !== null);
    return note.length ? note.reduce((a, p) => a + (p.spente ?? 0), 0) : null;
  }
  return punti.reduce((a, p) => a + p[metrica], 0);
}

export function serieAndamento(
  canali: CanalePanoramica[],
  metrica: Metrica,
  selezionato: string | null,
): Serie {
  const soloBuybox = METRICHE.find((m) => m.id === metrica)?.soloBuybox ?? false;
  const pool = canali.filter((c) => !soloBuybox || c.tipo === "buybox");
  const giorni = [...new Set(pool.flatMap((c) => c.andamento.map((p) => p.giorno)))].sort().slice(-30);
  const delGiorno = (c: CanalePanoramica, giorno: string) => c.andamento.filter((p) => p.giorno === giorno);
  const scelto = selezionato ? pool.find((c) => c.marketplace_id === selezionato) : undefined;
  return {
    giorni,
    totale: giorni.map((g) => valoreDelGiorno(pool.flatMap((c) => delGiorno(c, g)), metrica)),
    canale: scelto ? giorni.map((g) => valoreDelGiorno(delGiorno(scelto, g), metrica)) : null,
  };
}

export function niceStep(x: number): number {
  const potenza = Math.pow(10, Math.floor(Math.log10(x || 1)));
  for (const m of [1, 2, 2.5, 5, 10]) if (x / potenza <= m) return m * potenza;
  return 10 * potenza;
}

export function scala(valori: number[]): { lo: number; hi: number; passo: number } {
  if (valori.length === 0) return { lo: 0, hi: 1, passo: 1 };
  const minimo = Math.min(...valori);
  const massimo = Math.max(...valori);
  const passo = niceStep(Math.max((massimo - minimo) / 4, (massimo || 1) * 0.02));
  const lo = Math.max(0, Math.floor(minimo / passo) * passo);
  let hi = Math.ceil(massimo / passo) * passo;
  if (hi === lo) hi = lo + passo;
  return { lo, hi, passo };
}
